import { ReactQuery } from 'cms-api'
import Link from 'next/link'
import React from 'react'
import { ROUTE } from 'shared-constants'
import { Button, BUTTON_VARIANT } from 'components/Button'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import { Box, Flex } from 'components/Layout'
import { Text } from 'components/Typography'
import { getSelectedVariant } from 'components/UserFavorites/UserRecentlyViewedProducts'
import { WrappedLoader } from 'components/WrappedLoader'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_NAMES, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import { getProductRoute } from 'utils/getProductRoute'
import { getProductSlug, getVariantSlug } from 'utils/getProductSlug'
import { ProductLink } from './ProductLink'

export const ExpertFavoritesWidget = () => {
  const { user } = useUserAuth()
  const { data: recentlyViewedData, isLoading: isRecentlyViewedLoading } =
    ReactQuery.useGetRecentlyViewedUserProductHistory(
      {
        populate: {
          product: {
            populate: {
              productVariants: {
                populate: {
                  productVariantInventory: true,
                  images: true,
                  leadProductImage: true,
                },
              },
            },
          },
        } as never,
        'pagination[pageSize]': 4,
        sort: 'updatedAt:asc',
      },
      {
        query: {
          queryKey: ['recentlyViewed', user?.id],
        },
      }
    )

  const { data: favoritesData, isLoading: isFavoritesLoading } =
    ReactQuery.useGetFavoriteUserProductVariantBookmark(
      {
        populate: {
          productVariant: {
            populate: {
              product: true,
              images: true,
              optionValues: true,
            },
          },
        } as never,
        'pagination[pageSize]': 4,
        sort: 'updatedAt:desc',
      },
      {
        query: {
          queryKey: ['favorites', user?.id],
        },
      }
    )

  const hasFavorites = favoritesData?.data?.length > 0
  const hasRecentlyViewed = recentlyViewedData?.data?.length > 0

  return (
    <Card
      flexDirection="column"
      width="100%"
      height="100%"
      elevation={CARD_ELEVATION.NONE}
      hasBorders
      paddingSize={CARD_PADDING_SIZE.NONE}
    >
      <Box
        width="100%"
        backgroundColor="white"
        py={SPACE.PX_32}
        borderRadius={SPACE.PX_8}
        height="100%"
      >
        {isRecentlyViewedLoading || isFavoritesLoading ? (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <WrappedLoader />
          </Flex>
        ) : (
          <Box height="100%" width="100%">
            <Flex
              flexDirection={{ MOBILE: 'column', TABLET: 'row' }}
              height="100%"
              width="100%"
              gap={{ MOBILE: '40px', TABLET: '0' }}
            >
              <Box width={{ MOBILE: '100%', TABLET: '50%' }} height="100%">
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="space-between"
                  height="100%"
                >
                  <Box>
                    <Text
                      color={COLOR_NAMES.PINEY_LAKE}
                      fontWeight={FONT_WEIGHT.MEDIUM}
                      fontSize={FONT_SIZE.PX_18}
                      textAlign="center"
                      as="p"
                    >
                      Recently Favorited
                    </Text>
                    <Box px={SPACE.PX_16}>
                      {favoritesData?.data?.map((item) => (
                        <ProductLink
                          key={item.productVariant.id}
                          productImageUrl={item.productVariant.images[0].url}
                          productImageAlt={
                            item.productVariant.images[0].alternativeText
                          }
                          title={item.productVariant.product.title}
                          options={item?.productVariant?.optionValues?.map(
                            (option) => option.value
                          )}
                          href={getProductRoute({
                            slug: getProductSlug({
                              id: item.productVariant.product.legacyId,
                              title: item.productVariant.product.title,
                            }),
                            variantSlug: getVariantSlug({
                              id: item.productVariant.legacyId,
                              title: item.productVariant.product.title,
                            }),
                          })}
                        />
                      ))}
                    </Box>
                  </Box>
                  <Link
                    href={
                      hasFavorites
                        ? ROUTE.EXPERT_FAVORITES()
                        : ROUTE.SHOWROOM_PRODUCTS()
                    }
                  >
                    <Button
                      variant={BUTTON_VARIANT.OUTLINE}
                      width="200px"
                      mt="40px"
                    >
                      {hasFavorites ? 'View Favorites' : 'Start Favoriting'}
                    </Button>
                  </Link>
                </Flex>
              </Box>

              <Box
                width={{ MOBILE: '100%', TABLET: '50%' }}
                borderLeft={{ MOBILE: 'none', TABLET: '1px solid' }}
                borderColor={{ MOBILE: 'none', TABLET: COLOR_NAMES.JUPITER }}
                height="100%"
              >
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="space-between"
                  height="100%"
                >
                  <Box>
                    <Text
                      color={COLOR_NAMES.PINEY_LAKE}
                      fontWeight={FONT_WEIGHT.MEDIUM}
                      fontSize={FONT_SIZE.PX_18}
                      textAlign="center"
                      as="p"
                    >
                      Recently Viewed
                    </Text>
                    <Box px={SPACE.PX_16}>
                      {recentlyViewedData?.data.map(({ product }) => {
                        const mainVariant = getSelectedVariant(
                          product.productVariants
                        )

                        return (
                          <ProductLink
                            key={product.id}
                            productImageAlt={
                              mainVariant.leadProductImage?.alternativeText ||
                              product.title
                            }
                            productImageUrl={
                              mainVariant.leadProductImage?.url ||
                              mainVariant.images[0].url
                            }
                            title={product.title}
                            href={getProductRoute({
                              slug: getProductSlug({
                                id: product.legacyId,
                                title: product.title,
                              }),
                              variantSlug: getVariantSlug({
                                id: mainVariant.legacyId,
                                title: product.title,
                              }),
                            })}
                          />
                        )
                      })}
                    </Box>
                  </Box>
                  <Link
                    href={
                      hasRecentlyViewed
                        ? `${ROUTE.EXPERT_FAVORITES()}?tab=recently-viewed`
                        : ROUTE.SHOWROOM_PRODUCTS()
                    }
                  >
                    <Button
                      variant={BUTTON_VARIANT.OUTLINE}
                      width="200px"
                      mt="40px"
                    >
                      {hasRecentlyViewed
                        ? 'See recently viewed'
                        : 'Browse Products'}
                    </Button>
                  </Link>
                </Flex>
              </Box>
            </Flex>
          </Box>
        )}
      </Box>
    </Card>
  )
}
